import { createRouter, createWebHistory, RouterView } from "vue-router";
import Tr from "@/i18n/translation";

import { useUserStore } from "@/stores/user";

const checkAuth = (to, from, next) => {
  // Unless explicitely set in meta, all routes must be authed
  if (to.meta && to.meta.authNotRequired) next();
  else {
    const user = useUserStore();

    if (user.token) next();
    else {
      localStorage.setItem("redirectAfterLogin", to.path);
      next({ name: "loginAccess" });
    }
  }
};

// Publishing requires either video clips or compositions. Load the plan async
// and redirect to the assets library if both features are disabled.
const checkPublishing = async (_to, _from, next) => {
  const user = useUserStore();
  const plan = await user.currentPlan();
  if (plan && (plan.video_clips || plan.video_compositions)) next();
  else next("/app/assets");
};

// The editor requires the video_clips feature; redirect to assets otherwise.
const checkClips = async (_to, _from, next) => {
  const user = useUserStore();
  const plan = await user.currentPlan();
  if (plan && plan.video_clips) next();
  else next("/app/assets");
};

// The composer requires the video_compositions feature; redirect otherwise.
const checkCompositions = async (_to, _from, next) => {
  const user = useUserStore();
  const plan = await user.currentPlan();
  if (plan && plan.video_compositions) next();
  else next("/app/assets");
};

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/:locale?",
      component: RouterView,
      beforeEnter: [checkAuth, Tr.routeMiddleware],
      children: [
        {
          path: "",
          name: "login",
          meta: { authNotRequired: true },
          component: () => import("../views/LoginView.vue"),
          children: [
            {
              path: "",
              alias: "access",
              name: "loginAccess",
              component: () => import("../views/LoginAccessView.vue")
            },
            {
              path: "forgot",
              name: "loginForgotPass",
              component: () => import("../views/LoginForgotView.vue")
            },
            {
              path: "restore",
              name: "loginRestorePass",
              component: () => import("../views/LoginRestoreView.vue")
            },
            {
              path: "changed",
              name: "loginChangedPass",
              component: () => import("../views/LoginChangedView.vue")
            },
            {
              path: "tempPassword",
              name: "loginTempPassword",
              component: () => import("../views/LoginTempPasswordView.vue")
            },
            {
              path: "mfaSetup",
              name: "loginMfaSetup",
              component: () => import("../views/LoginMfaSetupView.vue")
            },
            {
              path: "mfa/:idSession",
              name: "loginMfa",
              component: () => import("../views/LoginMfaView.vue")
            },
            {
              path: "first/:idSession",
              name: "firstAccessWithTmpPass",
              component: () => import("../views/LoginFirstAccessView.vue")
            }
          ]
        },
        {
          path: "app",
          name: "app",
          component: () => import("../views/AppView.vue"),
          children: [
            {
              path: "account",
              component: () => import("../views/AccountView.vue"),
              children: [
                {
                  path: "company",
                  component: () => import("../views/CompanyView.vue"),
                  children: [
                    {
                      path: "details",
                      component: () => import("../views/CompanyDetailsView.vue")
                    },
                    {
                      path: "members",
                      component: () => import("../views/CompanyMembersView.vue")
                    }
                  ]
                },
                {
                  path: "settings",
                  component: () => import("../views/SettingsView.vue"),
                  // Sin sub-ruta, redirige al primer tab (antes quedaba vacío
                  // y sin tab activo). Mismo patrón que /app/publishing.
                  redirect: "/app/account/settings/templates",
                  children: [
                    {
                      path: "apiKeys",
                      component: () =>
                        import("../views/SettingsApiKeysView.vue")
                    },
                    {
                      path: "templates",
                      component: () =>
                        import("../views/SettingsTemplatesView.vue")
                    },
                    {
                      path: "externalStorage",
                      component: () =>
                        import("../views/SettingsExtStorageView.vue")
                    },
                    {
                      path: "socialMediaAccounts",
                      name: "settingsSocialAccounts",
                      component: () =>
                        import("../views/SettingsSocialAccountsView.vue"),
                      children: [
                        {
                          path: "completed",
                          name: "connectionComplete",
                          component: () =>
                            import(
                              "../views/SettingsSocialAccountsCompletedView.vue"
                            )
                        }
                      ]
                    }
                  ]
                },
                {
                  path: "profile",
                  component: () => import("../views/ProfileView.vue"),
                  children: [
                    {
                      path: "details",
                      component: () => import("../views/ProfileDetailsView.vue")
                    },
                    {
                      path: "password",
                      component: () =>
                        import("../views/ProfilePasswordView.vue")
                    },
                    {
                      path: "security",
                      component: () =>
                        import("../views/ProfileSecurityView.vue")
                    }
                  ]
                },
                {
                  path: "usage",
                  component: () => import("../views/UsageView.vue"),
                  children: [
                    {
                      path: "summary",
                      component: () => import("../views/UsageSummaryView.vue")
                    }
                  ]
                },
                {
                  path: "developer",
                  component: () => import("../views/DeveloperView.vue")
                },
                {
                  path: "cookies",
                  component: () => import("../views/CookiesView.vue")
                }
              ]
            },
            {
              path: "assets",
              alias: "media",
              component: () => import("../views/MediaView.vue"),
              children: [
                {
                  path: "",
                  component: () => import("../views/LibraryView.vue"),
                  meta: { section: "assets", mainSection: "media" }
                },
                {
                  path: "collections",
                  component: () => import("../views/CollectionsView.vue"),
                  meta: { section: "collections", mainSection: "media" }
                },
                {
                  name: "insideCollection",
                  path: "collections/:idCollection/:colName?",
                  component: () => import("../views/CollectionView.vue"),
                  meta: { section: "collections", mainSection: "media" }
                },
                {
                  path: "views",
                  component: () => import("../views/ViewsView.vue"),
                  meta: { section: "views", mainSection: "media" }
                },
                {
                  name: "insideView",
                  path: "views/:idView/:viewName?",
                  component: () => import("../views/ViewView.vue"),
                  meta: { section: "views", mainSection: "media" }
                }
              ]
            },
            {
              path: "live",
              component: () => import("../views/LiveView.vue"),
              meta: { section: "live", mainSection: "live" },
              children: [
                {
                  // Unified Live + Real-Time landing (channel cards + events).
                  path: "",
                  alias: "channels",
                  component: () => import("../views/LiveUnifiedView.vue"),
                  meta: { section: "channels", mainSection: "live" }
                },
                {
                  path: "channels/:idChannel/:start?/:end?",
                  component: () => import("../components/file/LiveEditor.vue"),
                  meta: { section: "channels", mainSection: "live" }
                },
                {
                  // Camera Groups — its own tab within Live.
                  path: "groups",
                  component: () => import("../views/CameraGroupsView.vue"),
                  meta: { section: "groups", mainSection: "live" }
                },
                {
                  // Legacy standalone Real-Time list, now merged into the
                  // unified view: redirect so old links/breadcrumbs still land.
                  path: "realtime",
                  redirect: "/app/live/channels"
                },
                {
                  path: "realtime/:idEvent/:eventName?",
                  component: () =>
                    import("../components/realtime/EventViewer.vue"),
                  meta: { section: "realtime", mainSection: "live" }
                }
              ]
            },
            {
              path: "dictionaries",
              meta: { mainSection: "dictionaries" },
              component: () => import("../views/DictionariesView.vue")
            },
            {
              path: "branding",
              meta: { mainSection: "branding" },
              component: () => import("../views/BrandingView.vue"),
              children: [
                {
                  path: "",
                  meta: { mainSection: "branding" },
                  component: () =>
                    import("../views/CustomizationBrandingView.vue")
                },
                {
                  path: "media",
                  meta: { mainSection: "branding" },
                  component: () => import("../views/CustomizationMediaView.vue")
                },
                {
                  path: "subtitles",
                  meta: { mainSection: "branding" },
                  component: () =>
                    import("../views/CustomizationSubtitlesView.vue")
                }
              ]
            },
            {
              path: "assets/:idAsset",
              component: () => import("../views/AssetView.vue"),
              meta: { mainSection: "insights" },
              children: [
                {
                  path: "",
                  alias: "insights",
                  meta: { mainSection: "insights" },
                  beforeEnter: async (to, _from, next) => {
                    // Insights require the metadata_generation feature. Load the
                    // plan async and redirect to transcriptions if it's disabled.
                    const user = useUserStore();
                    const plan = await user.currentPlan();
                    if (plan && plan.metadata_generation) next();
                    else
                      next(`/app/assets/${to.params.idAsset}/transcriptions`);
                  },
                  component: () => import("../views/InsightsView.vue")
                },
                {
                  path: "transcriptions",
                  meta: { mainSection: "transcriptions" },
                  component: () => import("../views/TranscriptionsView.vue")
                },
                {
                  path: "subtitles",
                  meta: { mainSection: "subtitles" },
                  component: () => import("../views/SubtitlesView.vue")
                },
                {
                  path: "dubbing",
                  meta: { mainSection: "dubbing" },
                  component: () => import("../views/DubbingView.vue")
                },
                {
                  path: "docs",
                  meta: { mainSection: "docs" },
                  component: () => import("../views/DocsView.vue")
                },
                {
                  path: "clipping/:start?/:end?",
                  name: "assetClip",
                  meta: { mainSection: "clipping" },
                  beforeEnter: checkClips,
                  component: () => import("../views/EditorView.vue")
                }
              ]
            },
            {
              // Legacy URL → new nested clipping path under the asset.
              path: "editor/:idAsset/:start?/:end?",
              redirect: to => {
                const { idAsset, start, end } = to.params;
                const parts = [`/app/assets/${idAsset}/clipping`];
                if (start) parts.push(start);
                if (end) parts.push(end);
                return parts.join("/");
              }
            },
            {
              path: "composer/:idComposition?",
              meta: { mainSection: "composer" },
              beforeEnter: checkCompositions,
              component: () => import("../views/ComposerView.vue")
            },
            {
              path: "publishing",
              meta: { mainSection: "publishing" },
              component: () => import("../views/PublishingView.vue"),
              redirect: "/app/publishing/posts",
              children: [
                {
                  path: "posts",
                  meta: { mainSection: "publishing" },
                  beforeEnter: checkPublishing,
                  component: () =>
                    import("../views/PublishingPublishedView.vue")
                },
                {
                  path: "scheduled",
                  meta: { mainSection: "publishing" },
                  beforeEnter: checkPublishing,
                  component: () =>
                    import("../views/PublishingScheduledView.vue")
                }
              ]
            },
            {
              path: "search/:searchText?",
              name: "search",
              component: () => import("../views/SearchView.vue")
            },
            {
              path: "notifications",
              meta: { mainSection: "notifications" },
              component: () => import("../views/NotificationsView.vue")
            }
          ]
        },
        {
          path: "signup",
          name: "signup",
          meta: { authNotRequired: true },
          component: () => import("../views/SignupView.vue")
        },
        {
          path: "verify_login",
          meta: { authNotRequired: true },
          component: () => import("../views/VerifyLoginView.vue")
        },
        {
          path: "embedded",
          meta: { authNotRequired: true },
          component: () => import("../views/EmbeddedMode.vue")
        },
        {
          path: "/:locale?/:pathMatch(.*)*",
          meta: { authNotRequired: true },
          component: () => import("../views/NotFound.vue")
        }
      ]
    }
  ]
});

export default router;
